// Customer-scoped desktop shell: sidebar on the left; header, deploy
// banner and the page on the right. The sidebar IS the navigation.
//
// The shell paints an ambient light layer (from the theme) behind
// everything. It is what the Glass sidebar's backdrop-filter refracts —
// without it "liquid glass" is just a tinted rectangle. Earth and
// Midnight turn blur off (html[data-flat]) and read as flat surfaces.
//
// Phones never see this shell: below 768px every desktop URL is sent to
// the matching companion screen, query and hash included
// (/settings#whatsapp opens WhatsApp there too, /subagents?tab=runs Runs).

import { useEffect } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";

import { phoneLocationFor } from "@/companion/paths";
import { DeployBanner } from "@/components/ui/DeployBanner";
import { FOCUS_RING, cx } from "@/components/ui/controlStyles";
import { RecordingSlot } from "@/features/meetings/recorder/RecordingBar";
import { CustomerSidebar } from "@/features/navigation/CustomerSidebar";
import { MacShellWiring } from "@/features/navigation/MacWindowControls";
import { StartWorkHost } from "@/features/startwork/StartWorkHost";
import { useIsMobile } from "@/hooks/useMediaQuery";
import { isMacShell } from "@/native/desktop";
import { useSelection } from "@/stores/selection";
import { ambientLight } from "@/theme";

import { PageKeys } from "@/features/navigation/PageKeys";
import { useDocumentTitle } from "@/features/navigation/usePageName";

import { CustomerHeader } from "./CustomerHeader";

interface CustomerShellProps {
  customerId: string;
}

const AMBIENT_LIGHT: React.CSSProperties = { background: ambientLight };

function skipToMain(e: React.MouseEvent<HTMLAnchorElement>) {
  e.preventDefault();
  document.getElementById("main")?.focus();
}

export function CustomerShell({ customerId }: CustomerShellProps) {
  const mobile = useIsMobile();
  const { pathname, search, hash } = useLocation();
  const { setCustomer } = useSelection();
  useDocumentTitle(customerId);

  useEffect(() => {
    setCustomer(customerId);
    return () => setCustomer(null);
  }, [customerId, setCustomer]);

  // After the hooks, so hook order stays stable across the breakpoint.
  if (mobile) return <Navigate to={{ ...phoneLocationFor(pathname, search), hash }} replace />;

  return (
    <div className="relative flex app-vh w-full overflow-hidden text-fg" style={AMBIENT_LIGHT}>
      {/* The first thing a keyboard reaches: straight past the sidebar and
          the top bar. Focus moves in code, so the URL's hash (Settings keeps
          its section there) is left alone. */}
      <a
        href="#main"
        onClick={skipToMain}
        className={cx(
          isMacShell()
            ? // In the Mac app, clear of the window buttons in the corner.
              "sr-only focus:not-sr-only focus:absolute focus:left-[88px] focus:top-3 focus:z-50"
            : "sr-only focus:not-sr-only focus:absolute focus:left-3 focus:top-3 focus:z-50",
          "focus:rounded-control focus:border focus:border-line focus:bg-overlay focus:px-3 focus:py-2 focus:text-sm focus:text-fg",
          FOCUS_RING,
        )}
      >
        Skip to content
      </a>
      <CustomerSidebar customerId={customerId} />
      <PageKeys />
      {/* The Mac app's window: where its top row drags, and its View menu's
          Toggle Sidebar (native/desktop.ts). Renders nothing. */}
      {isMacShell() && <MacShellWiring />}
      {/* The content column carries the top/right safe-area insets (0 in
          a browser tab). */}
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden pt-safe pr-safe">
        <CustomerHeader customerId={customerId} />
        <DeployBanner />
        {/* The recording (live, uploading, or asking "Carry on?") shows here, above the page, never over it. */}
        <RecordingSlot />
        {/* "Give a task" over any page: a page adds ?give=… to its own URL (features/startwork). */}
        <StartWorkHost />
        <main id="main" tabIndex={-1} className="min-h-0 flex-1 overflow-hidden outline-none">
          {/* Pages that fill with `absolute inset-0` are bounded by this box. */}
          <div className="relative h-full min-h-0">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
